import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";
import api from "@/lib/api";
import { Loading } from "@/pages/_shared";

const CAT_ACCENT = {
  "Getting Started": "#1a5fd0",
  "Personal Credit": "#059669",
  "Business Credit": "#b0862f",
  "Funding & Capital": "#7c3aed",
};

export default function LearningArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let ok = true;
    setArticle(null);
    setNotFound(false);
    api.get(`/learning/articles/${slug}`)
      .then((r) => { if (ok) setArticle(r.data); })
      .catch(() => { if (ok) setNotFound(true); });
    return () => { ok = false; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#f7f4ec]" data-testid="learning-article-notfound">
        <PublicNav />
        <div className="mx-auto max-w-3xl px-5 py-24 text-center">
          <h1 className="font-serif-display text-3xl font-semibold text-slate-900">Article not found</h1>
          <p className="mt-3 text-[14px] text-slate-500">This guide may have been moved or unpublished.</p>
          <button onClick={() => navigate("/learning")} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#0f172a] px-5 py-3 text-[13px] font-semibold text-white"><ArrowLeft size={15} /> Back to Learning</button>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#f7f4ec]"><PublicNav /><Loading /></div>
    );
  }

  const accent = CAT_ACCENT[article.category] || "#1a5fd0";

  return (
    <div className="min-h-screen bg-[#f7f4ec]" data-testid="learning-article-page">
      <PublicNav />
      <article className="mx-auto max-w-3xl px-5 py-12 lg:px-8">
        <Link to="/learning" data-testid="article-back" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 transition-colors hover:text-[#1a5fd0]"><ArrowLeft size={15} /> All guides</Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider" style={{ background: `${accent}15`, color: accent }}>{article.category}</span>
          {article.read_minutes ? <span className="flex items-center gap-1 font-mono text-[11px] text-slate-400"><Clock size={12} />{article.read_minutes} min read</span> : null}
        </div>
        <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight tracking-tight text-slate-900 lg:text-5xl">{article.title}</h1>
        {article.excerpt && <p className="mt-4 text-lg leading-relaxed text-slate-500">{article.excerpt}</p>}
        <div className="mt-3 font-mono text-[11px] uppercase tracking-wider text-slate-400">By {article.author || "MMT Editorial"}</div>

        <div className="learning-prose mt-8 border-t border-slate-200 pt-8" data-testid="article-body">
          <ReactMarkdown>{article.body}</ReactMarkdown>
        </div>

        {article.related?.length > 0 && (
          <div className="mt-12 border-t border-slate-200 pt-8">
            <div className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#a37d2a]">Keep reading</div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {article.related.map((r) => (
                <Link key={r.slug} to={`/learning/${r.slug}`} className="group rounded-xl border border-slate-200/80 bg-white p-5 transition-all hover:border-slate-300 hover:shadow-sm">
                  <h3 className="font-head text-[15px] font-bold text-slate-900">{r.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{r.excerpt}</p>
                  <span className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#1a5fd0] transition-all group-hover:gap-2.5">Read <ArrowRight size={13} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-[#0f172a] p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-serif-display text-xl font-medium text-white">Ready to put this into action?</div>
            <p className="mt-1 text-[14px] text-slate-300">Create a free account and start building today.</p>
          </div>
          <Link to="/register" className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#b0862f] to-[#d8b25a] px-6 py-3 text-[13px] font-semibold text-[#0f172a] active:scale-95">Get Started Free <ArrowRight size={15} /></Link>
        </div>
      </article>
      <PublicFooter />
    </div>
  );
}
